import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Copy, Check, Loader2, ShieldCheck, UserCheck, UserX, Users, Stethoscope, DollarSign, Wallet, ChevronDown, ChevronRight, Download, Trash2, KeyRound, Network } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;

export default function AdminPanel() {
  const [reps, setReps] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tree, setTree] = useState(null);
  const [treeOpen, setTreeOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downline, setDownline] = useState(null);
  const [downlineLoading, setDownlineLoading] = useState(false);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reps", { params: q ? { q } : {} });
      setReps(data.reps || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load reps");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOverview = useCallback(async () => {
    try {
      const [ov, tr] = await Promise.all([api.get("/admin/overview"), api.get("/admin/tree")]);
      setOverview(ov.data);
      setTree(tr.data);
    } catch (err) {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    load();
    loadOverview();
  }, [load, loadOverview]);

  const onSearch = (e) => {
    e.preventDefault();
    load(query.trim());
  };

  const refLink = (code) => `${window.location.origin}/?ref=${code}`;

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/reps/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "afha_reps.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Rep list exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(refLink(code));
    setCopied(code);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(null), 1500);
  };

  const toggleActivation = async (rep) => {    setBusyId(rep.id);
    try {
      const { data } = await api.put(`/admin/reps/${rep.id}/activation`, { activated: !rep.activated });
      setReps((prev) => prev.map((r) => (r.id === rep.id ? { ...r, activated: data.activated } : r)));
      toast.success(data.activated ? `${rep.name} activated` : `${rep.name} deactivated`);
      loadOverview();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const viewDownline = async (rep) => {
    setDownlineLoading(true);
    setDownline({ rep, sponsor: null, levels: {}, team_size: 0 });
    try {
      const { data } = await api.get(`/admin/reps/${rep.id}/downline`);
      setDownline(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load downline");
      setDownline(null);
    } finally {
      setDownlineLoading(false);
    }
  };

  const resetPassword = async (rep) => {
    setBusyId(rep.id);
    try {
      const { data } = await api.post(`/admin/reps/${rep.id}/reset-password`);
      if (data.reset_link) {
        navigator.clipboard.writeText(data.reset_link).catch(() => {});
      }
      toast.success(`Reset link emailed to ${rep.email} — link also copied to your clipboard`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/reps/${deleteTarget.id}`);
      setReps((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      loadOverview();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-panel">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-navy text-white flex items-center justify-center">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h1 className="font-display text-3xl text-navy tracking-tight">AFHA HQ — Admin</h1>
          <p className="text-muted-foreground text-sm mt-1">Search reps, copy referral links, and manually activate accounts.</p>
        </div>
        <Button onClick={exportCsv} data-testid="export-csv-btn" variant="outline" className="ml-auto rounded-full gap-2 border-navy text-navy hover:bg-navy hover:text-white">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-overview">
          {[
            { label: "Total reps", value: overview.total_reps, sub: `${overview.activated_reps} active · ${overview.pending_reps} pending`, icon: Users },
            { label: "Physicians onboarded", value: overview.total_physicians, sub: "across all reps", icon: Stethoscope },
            { label: "Overrides paid", value: money(overview.override_paid), sub: `personal: ${money(overview.personal_paid)}`, icon: DollarSign },
            { label: "Activation fees", value: money(overview.activation_fees_total), sub: `${overview.activation_fees_count} paid`, icon: Wallet },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-white p-4" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
                <s.icon className="h-4 w-4 text-navy/60" />
              </div>
              <p className="font-display text-2xl text-navy mt-2">{s.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>
            </div>
          ))}
        </div>
      )}

      {tree && (
        <div className="rounded-xl border border-border bg-white" data-testid="org-tree">
          <button
            onClick={() => setTreeOpen((o) => !o)}
            data-testid="toggle-tree"
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <span className="font-display text-lg text-navy">
              Organization tree {tree.root ? `· ${tree.root.name}` : ""}
            </span>
            {treeOpen ? <ChevronDown className="h-5 w-5 text-navy" /> : <ChevronRight className="h-5 w-5 text-navy" />}
          </button>
          {treeOpen && (
            <div className="px-4 pb-5 space-y-4">
              {[1, 2, 3, 4, 5, 6].map((lvl) => {
                const members = (tree.levels && tree.levels[lvl]) || [];
                if (members.length === 0) return null;
                return (
                  <div key={lvl} data-testid={`tree-level-${lvl}`}>
                    <p className="text-xs uppercase tracking-wider text-patriot font-bold mb-2">Level {lvl} · {members.length}</p>
                    <div className="flex flex-wrap gap-2">
                      {members.map((m) => (
                        <div key={m.id} className="rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm">
                          <span className="font-semibold text-navy">{m.name}</span>
                          <span className="text-muted-foreground"> · {m.physicians} phys</span>
                          <span className="block text-xs font-mono text-muted-foreground">{m.referral_code}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {[1, 2, 3, 4, 5, 6].every((lvl) => !((tree.levels && tree.levels[lvl]) || []).length) && (
                <p className="text-sm text-muted-foreground">No downline members yet.</p>
              )}
            </div>
          )}
        </div>
      )}

      <form onSubmit={onSearch} className="flex gap-3 max-w-xl" data-testid="admin-search-form">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or referral code"
            data-testid="admin-search-input"
            className="pl-9 h-11"
          />
        </div>
        <Button type="submit" data-testid="admin-search-btn" className="rounded-full bg-navy hover:bg-navy-dark text-white px-6">
          Search
        </Button>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-20" data-testid="admin-loading">
          <Loader2 className="h-7 w-7 animate-spin text-navy" />
        </div>
      ) : reps.length === 0 ? (
        <p className="text-muted-foreground" data-testid="admin-empty">No representatives found.</p>
      ) : (
        <div className="space-y-3" data-testid="admin-reps-list">
          {reps.map((rep) => (
            <div
              key={rep.id}
              data-testid={`admin-rep-${rep.id}`}
              className="rounded-xl border border-border bg-white p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-navy">{rep.name}</p>
                  {rep.activated ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid={`status-${rep.id}`}>Activated</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid={`status-${rep.id}`}>Not activated</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate">{rep.email}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded" data-testid={`code-${rep.id}`}>{rep.referral_code}</span>
                  <span>· {rep.recruits} recruits</span>
                  <span>· {rep.physicians} physicians</span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  onClick={() => copyLink(rep.referral_code)}
                  data-testid={`copy-ref-${rep.id}`}
                  className="rounded-full gap-2 border-navy text-navy hover:bg-navy hover:text-white"
                >
                  {copied === rep.referral_code ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  Referral link
                </Button>
                <Button
                  onClick={() => toggleActivation(rep)}
                  disabled={busyId === rep.id}
                  data-testid={`toggle-activation-${rep.id}`}
                  className={`rounded-full gap-2 text-white ${rep.activated ? "bg-patriot hover:bg-patriot/90" : "bg-green-600 hover:bg-green-700"}`}
                >
                  {busyId === rep.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : rep.activated ? (
                    <><UserX className="h-4 w-4" /> Deactivate</>
                  ) : (
                    <><UserCheck className="h-4 w-4" /> Activate</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => viewDownline(rep)}
                  data-testid={`view-downline-${rep.id}`}
                  className="rounded-full gap-2 border-navy text-navy hover:bg-navy hover:text-white"
                >
                  <Network className="h-4 w-4" /> Downline
                </Button>
                <Button
                  variant="outline"
                  onClick={() => resetPassword(rep)}
                  disabled={busyId === rep.id}
                  data-testid={`reset-password-${rep.id}`}
                  className="rounded-full gap-2 border-navy text-navy hover:bg-navy hover:text-white"
                >
                  <KeyRound className="h-4 w-4" /> Reset password
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeleteTarget(rep)}
                  data-testid={`delete-rep-${rep.id}`}
                  className="rounded-full gap-2 border-patriot text-patriot hover:bg-patriot hover:text-white"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-rep-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {deleteTarget?.email} along with their physicians and earnings.
              Any recruits under them move up to their sponsor. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              data-testid="delete-confirm"
              className="bg-patriot hover:bg-patriot/90 text-white gap-2"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete rep
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!downline} onOpenChange={(o) => !o && setDownline(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="downline-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-navy">
              {downline?.rep?.name}'s downline
            </DialogTitle>
            <DialogDescription>
              {downline?.rep?.referral_code} · {downline?.rep?.email}
              {" · "}team of {downline?.team_size ?? 0}
            </DialogDescription>
          </DialogHeader>

          {downlineLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted/60 border border-border px-4 py-3 text-sm" data-testid="downline-sponsor">
                <span className="text-muted-foreground">Sponsor (upline): </span>
                {downline?.sponsor ? (
                  <span className="font-semibold text-navy">{downline.sponsor.name} · {downline.sponsor.referral_code}</span>
                ) : (
                  <span className="font-semibold text-navy">None (top of tree / direct)</span>
                )}
              </div>

              {[1, 2, 3, 4, 5, 6].map((lvl) => {
                const members = (downline?.levels && downline.levels[lvl]) || [];
                if (members.length === 0) return null;
                return (
                  <div key={lvl} data-testid={`downline-level-${lvl}`}>
                    <p className="text-xs uppercase tracking-wider text-patriot font-bold mb-2">Level {lvl} · {members.length}</p>
                    <div className="flex flex-wrap gap-2">
                      {members.map((m) => (
                        <div key={m.id} className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
                          <span className="font-semibold text-navy">{m.name}</span>
                          <span className="text-muted-foreground"> · {m.physicians} phys</span>
                          <span className="block text-xs font-mono text-muted-foreground">{m.referral_code}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {[1, 2, 3, 4, 5, 6].every((lvl) => !((downline?.levels && downline.levels[lvl]) || []).length) && (
                <p className="text-sm text-muted-foreground py-4" data-testid="downline-empty">This rep has no downline members yet.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
